import React, { memo } from "react";
import { Handle, Position } from "react-flow-renderer";
import { Typography, Chip } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { ContentCopy, Delete, ArrowForwardIos } from "@mui/icons-material";
import { useNodeStorage } from "../../../stores/useNodeStorage";
import { SiOpenai, SiGoogle } from "react-icons/si";
import { TbBrandOpenai } from "react-icons/tb";
import { FaRobot } from "react-icons/fa";

// Cores para cada provedor
const providerColors = {
  openai: "#10a37f",
  gemini: "#4285f4",
  anthropic: "#cc785c",
  local: "#6b7280",
};

// Ícones para cada provedor
const ProviderIcon = ({ provider, size = 18 }) => {
  const iconStyle = { width: size, height: size };
  switch (provider) {
    case "openai":
      return <SiOpenai style={{ ...iconStyle, color: providerColors.openai }} />;
    case "gemini":
      return <SiGoogle style={{ ...iconStyle, color: providerColors.gemini }} />;
    case "anthropic":
      return <TbBrandOpenai style={{ ...iconStyle, color: providerColors.anthropic }} />;
    case "local":
      return <FaRobot style={{ ...iconStyle, color: providerColors.local }} />;
    default:
      return <FaRobot style={{ ...iconStyle, color: "#6b7280" }} />;
  }
};

function IAAgentNode({ data, isConnectable, id }) {
  const theme = useTheme();
  const storageItems = useNodeStorage();

  const provider = data?.agentConfig?.provider || "openai";
  const model = data?.agentConfig?.model || "";
  const flowMode = data?.agentConfig?.flowMode || "permanent";
  const enableTools = data?.agentConfig?.enableTools || false;
  const toolsCount = (data?.agentConfig?.tools || []).length;
  const borderColor = providerColors[provider] || "#6b7280";

  return (
    <div
      style={{
        backgroundColor: theme.palette.background.paper,
        padding: "12px",
        borderRadius: "8px",
        boxShadow: theme.shadows[3],
        border: `2px solid ${borderColor}`,
        minWidth: "180px",
        maxWidth: "220px",
        position: "relative",
      }}
    >
      {/* Handle de entrada */}
      <Handle
        type="target"
        position={Position.Left}
        style={{
          background: "transparent",
          width: "28px",
          height: "28px",
          top: "50%",
          left: "-14px",
          cursor: "crosshair",
          border: "none",
        }}
        isConnectable={isConnectable}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              background: `linear-gradient(145deg, ${borderColor} 0%, ${borderColor}dd 100%)`,
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              boxShadow: theme.shadows[2],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "none",
            }}
          >
            <ArrowForwardIos
              style={{
                color: "#fff",
                width: "12px",
                height: "12px",
                transform: "rotate(180deg)",
              }}
            />
          </div>
        </div>
      </Handle>

      {/* Botões de ação */}
      <div
        style={{
          display: "flex",
          position: "absolute",
          right: 5,
          top: 5,
          cursor: "pointer",
          gap: 6,
        }}
      >
        <ContentCopy
          onClick={() => {
            storageItems.setNodesStorage(id);
            storageItems.setAct("duplicate");
          }}
          style={{
            width: "14px",
            height: "14px",
            color: theme.palette.primary.main,
          }}
        />
        <Delete
          onClick={() => {
            storageItems.setNodesStorage(id);
            storageItems.setAct("delete");
          }}
          style={{
            width: "14px",
            height: "14px",
            color: theme.palette.error.main,
          }}
        />
      </div>

      {/* Header com ícone e label */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "8px",
        }}
      >
        <ProviderIcon provider={provider} size={22} />
        <Typography
          variant="subtitle2"
          style={{
            fontWeight: "bold",
            color: theme.palette.text.primary,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          Agente IA
        </Typography>
      </div>

      {/* Info do modelo */}
      <div
        style={{
          backgroundColor:
            theme.palette.type === "dark"
              ? "rgba(255, 255, 255, 0.05)"
              : "rgba(0, 0, 0, 0.03)",
          padding: "6px 8px",
          borderRadius: "6px",
          marginBottom: "6px",
        }}
      >
        <Typography
          variant="caption"
          style={{
            color: theme.palette.text.secondary,
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {model || "Modelo não definido"}
        </Typography>
      </div>

      {/* Chips de status */}
      <div
        style={{
          display: "flex",
          gap: "4px",
          flexWrap: "wrap",
        }}
      >
        <Chip
          label={provider.toUpperCase()}
          size="small"
          style={{
            backgroundColor: borderColor,
            color: "#fff",
            fontSize: "10px",
            height: "20px",
          }}
        />
        <Chip
          label={flowMode === "permanent" ? "Permanente" : "Temporário"}
          size="small"
          variant="outlined"
          style={{
            fontSize: "10px",
            height: "20px",
            borderColor: flowMode === "temporary" ? "#ff9800" : borderColor,
            color: flowMode === "temporary" ? "#ff9800" : borderColor,
          }}
        />
        {enableTools && toolsCount > 0 && (
          <Chip
            label={`${toolsCount} tools`}
            size="small"
            variant="outlined"
            style={{
              fontSize: "10px",
              height: "20px",
              borderColor: borderColor,
              color: borderColor,
            }}
          />
        )}
      </div>

      {/* Handle de saída */}
      <Handle
        type="source"
        position={Position.Right}
        id="a"
        style={{
          background: "transparent",
          width: "28px",
          height: "28px",
          top: "50%",
          right: "-14px",
          cursor: "crosshair",
          border: "none",
        }}
        isConnectable={isConnectable}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              background: `linear-gradient(145deg, ${borderColor} 0%, ${borderColor}dd 100%)`,
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              boxShadow: theme.shadows[2],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "none",
            }}
          >
            <ArrowForwardIos
              style={{
                color: "#fff",
                width: "12px",
                height: "12px",
              }}
            />
          </div>
        </div>
      </Handle>
    </div>
  );
}

export default memo(IAAgentNode);
